import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { select, Store } from '@ngrx/store';
import { Router } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { ToastrService } from 'ngx-toastr';
import { DeleteConfirmationComponent } from 'src/app/redeem/modal/delete-confirmation/delete-confirmation.component';
import { ApiService } from 'src/app/services/api-services/api.service';
import { selectOrgSettings } from 'src/app/store/auth/auth.selector';
import {
  showError,
  showSuccess,
  showWarning,
} from 'src/app/utility/common-logic';
import { OrganizationSettings } from 'src/app/utility/model/organization';
import { environment } from 'src/environments/environment';
import { setSpinner, updateCartAll } from 'src/app/store/cart/cart.actions';
import { takeUntil } from 'rxjs/operators';
import { Subject } from 'rxjs';
import { selectCartDetails } from 'src/app/store/cart/cart.selectors';
import { AutoUnsubscribeComponent } from 'src/app/utility/auto-unsubscribe.component';
import { Cart, CartDetails } from 'src/app/utility/model/cart';

enum SignE {
  minus,
  plus,
}

@Component({
  selector: 'app-cart',
  templateUrl: './cart.component.html',
  styleUrls: ['./cart.component.scss'],
})
export class CartComponent extends AutoUnsubscribeComponent implements OnInit {
  @Input() editable: boolean;
  @Input() cartDetails: CartDetails;
  @Input() cartItems: Cart[];
  @Input() shippingId: any;
  cartCount: number;
  imgUrl = environment.url.img;
  @Output() receivedCartChanges = new EventEmitter<any>();
  @Output() closeCartDialog = new EventEmitter<any>();
  SignE = SignE;
  allowPayment = environment.settings.allowPayment;
  gateway: string | null;
  maxQty = environment.settings.rewards.maxQty;
  maxQtyCart = environment.settings.rewards.maxQtyCart;
  cartTotalQuantity: number = 0;
  orgSettings: OrganizationSettings;
  paymentGateway: string = '';

  constructor(
    private api: ApiService,
    private translate: TranslateService,
    public dialog: MatDialog,
    private toastr: ToastrService,
    private store: Store,
    private router: Router,
  ) {
    super();
  }

  ngOnInit(): void {
    this.store
      .pipe(select(selectCartDetails), takeUntil(this.destroy$))
      .subscribe((res) => {
        this.cartItems = Array.isArray(res) ? res : res?.results || [];
      });
    this.gateway = this.allowPayment ? 'ntt' : null;
    this.cartTotalQuantity = this.cartItems?.reduce(
      (total, item) => total + item.quantity,
      0,
    );
    this.store.pipe(select(selectOrgSettings)).subscribe((settings) => {
      this.orgSettings = settings;
    });
    if (this.orgSettings?.enable_multiple_redemptions == 1) {
      this.maxQty = 20;
      this.maxQtyCart = 60;
    }
    this.gateway = this.orgSettings?.gateway;
  }

  async getCartDetails() {
    this.store.dispatch(setSpinner({ spinner: true }));
    await this.api
      .getCartDetails()
      .then(async (res: any) => {
        this.cartDetails = res;
        this.cartCount = res?.count;
        this.cartTotalQuantity = res?.results.reduce(
          (total, item) => total + item.quantity,
          0,
        );
        this.store.dispatch(
          updateCartAll({
            cartDetails: res,
            cartCount: res?.count,
            cartStatus: res?.status,
            cartItems: res?.results,
          }),
        );
        this.receivedCartChanges.emit({
          cartDetails: this.cartDetails,
          cartItems: this.cartItems,
          count: res.count,
        });
        if (this.cartCount === 0 || res?.all_voucher_cart === true) {
          localStorage.setItem('MOVED_TO_EMPTY_CART', 'yes');
          this.router.navigate(['/my-cart']);
        }
      })
      .catch((err) => showError(err, this.toastr, this.translate))
      .finally(() => this.store.dispatch(setSpinner({ spinner: false })));
  }

  onRemoveConfirm(item) {
    const dialogRef = this.dialog.open(DeleteConfirmationComponent, {
      data: {
        message: this.translate.instant('msg.item.delete'),
        buttonText: {
          ok: this.translate.instant('common.delete'),
          cancel: this.translate.instant('common.cancel'),
        },
      },
    });
    dialogRef.afterClosed().subscribe((confirmed: boolean) => {
      if (confirmed) {
        this.removeFromCart(item);
      }
    });
  }

  async removeFromCart(item) {
    this.store.dispatch(setSpinner({ spinner: true }));
    const updateDate = { quantity: 0, gateway: this.gateway };
    await this.api
      .updateCart(item.id, updateDate)
      .then((res) => {
        showSuccess(
          'cart.msgRemovedSuccess',
          this.toastr,
          this.translate,
          true,
        );
        this.cartItems = this.cartItems.filter((o) => o.id !== item.id);
        this.getCartDetails();
      })
      .catch((err) =>
        showError(
          'cart.msgRemovedFailed',
          this.toastr,
          this.translate,
          true,
          true,
        ),
      )
      .finally(() => this.store.dispatch(setSpinner({ spinner: false })));
  }

  async changeQty(item: any, type: SignE) {
    if (
      (type === SignE.minus && item.quantity == 1) ||
      (type === SignE.plus &&
        (item.quantity == this.maxQty ||
          this.cartTotalQuantity == this.maxQtyCart))
    ) {
      const message =
        item.quantity == this.maxQty
          ? this.translate.instant('msg.qty.maxRewardQuantity', {
              qty: this.maxQty,
            })
          : item.quantity > 1
            ? this.translate.instant('msg.qty.cartMaxQuantity', {
                qty: this.cartTotalQuantity,
              })
            : '';
      if (message) showWarning(message, this.toastr, this.translate, true);
      return;
    }
    const quantity = type == SignE.plus ? item.quantity + 1 : item.quantity - 1;
    const updateDate = {
      quantity: quantity,
      gateway: this.gateway,
      shipping_id: this.shippingId,
    };
    await this.api
      .updateCart(item.id, updateDate)
      .then((res: any) => {
        this.updateCartItem(res);
      })
      .catch((err) => {
        showError(err, this.toastr, this.translate, false);
      })
      .finally(() => this.store.dispatch(setSpinner({ spinner: false })));
  }

  updateCartItem(item) {
    this.cartItems = this.cartItems.map((o) => (o.id === item.id ? item : o));

    this.cartDetails = {
      ...this.cartDetails,
      results: this.cartItems,
      cart: item?.cart,
      amount_payable: item?.amount_payable,
      user_points: item?.user_points,
      all_voucher_cart: item?.all_voucher_cart,
      calculated_points: item?.calculated_points,
      real_money: item?.real_money,
    };

    this.cartTotalQuantity = this.cartItems.reduce(
      (total, item) => total + item.quantity,
      0,
    );

    this.receivedCartChanges.emit({
      cartDetails: this.cartDetails,
      cartItems: this.cartItems,
      count: item?.items_count,
    });

    this.store.dispatch(
      updateCartAll({
        cartDetails: this.cartDetails,
        cartCount: item?.items_count,
        cartStatus: this.cartDetails.status,
        cartItems: this.cartItems,
      }),
    );
  }

  navigateToRewardDetail(item) {
    this.router.navigate([`/reward/${item?.reward_details?.pk}`]);
    this.closeCartDialog.emit();
  }
}
